import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { clinicalTrialSubRoutes } from '@router/ClinicalTrial.routes';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { twMerge } from '@utils/twMerge';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { routesPath } from '@router/routes';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { useNavigationBlocker } from '@hooks/useNavigationBlocker';
import { BackButton } from '@dynamika-shared/ui/BackButton/BackButton';
import Tooltip from 'antd/lib/tooltip';
import { Attention } from '@dynamika-shared/ui/icons';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { ChildrenCreator, generateTabsItems } from '@utils/generateTabsItems';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';

import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useActivateClinicalTrialSlice } from '@restore/activate/activateClinicalTrialSlice';
import { ActivateState } from '@restore/activate/activate.types';
import { useInventorySlice } from '@restore/inventory/inventory.slice';
import { BottomConfirmation } from 'src/components/BottomConfirmation/BottomConfirmation';
import { PageLoading } from '@dynamika-shared/ui/PageLoading/PageLoading';
import { ClinicalTrialActivateModal } from 'src/components/modals/ClinicalTrialActivate/ClinicalTrialActivate';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';
import { ACTIVATION_ERROR_MESSAGES } from 'src/constants/activationMessages';
import { useClinicalTrialContextSlice } from 'src/store-redux/clinicalTrialContext/clinicalTrialContext.slice';
import { ClinicalTrialNavRoute } from 'src/pages/ClinicalTrial/ClinicalTrial/types';

type AppRouteType = (typeof clinicalTrialSubRoutes)[number];

type ClinicalTrialProps = {
  children?: ReactNode;
};

function filterRoutesByNavRole<
  T extends { navAllowedRoles?: UserRoles[]; children?: T[] },
>(routes: ReadonlyArray<T>, role?: UserRoles): T[] {
  const canSee = (r: T) =>
    !r.navAllowedRoles || (role ? r.navAllowedRoles.includes(role) : true);

  return routes
    .filter(canSee)
    .map(r =>
      r.children
        ? { ...r, children: filterRoutesByNavRole(r.children, role) }
        : r
    );
}

export const ClinicalTrial: FC<ClinicalTrialProps> = ({ children }) => {
  const selectedRole = useAuthSlice(s => s?.data?.selectedRole) as
    | UserRoles
    | undefined;
  const isPM = selectedRole === UserRoles.PROJECT_MANAGER;

  const { pathname } = useLocation();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { blockerState, shouldBeBlocked, reset, proceed } =
    useNavigationBlocker();

  const [currentPathname, setCurrentPathname] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [
    currentTrial,
    fetchClinicalTrial,
    clinicalTrialLoading,
    clinicalTrialError,
  ] = useClinicalTrialSlice((state, actions) => [
    state.data,
    actions.fetchClinicalTrial,
    state.loading,
    state.error,
  ]);
  const [openModal, setOpenModal] = useState(false);

  const [fetchContext] = useClinicalTrialContextSlice((state, actions) => [
    actions.fetchClinicalTrialContext,
    actions.clearData,
    state.data,
  ]);

  const visibleSubRoutes = useMemo(() => {
    return filterRoutesByNavRole(
      clinicalTrialSubRoutes as unknown as ReadonlyArray<ClinicalTrialNavRoute>,
      selectedRole
    ) as unknown as typeof clinicalTrialSubRoutes;
  }, [selectedRole]);

  const goBack = () => {
    navigate(routesPath.clinicalTrials);
  };

  useEffect(() => {
    if (id) {
      fetchClinicalTrial(id);
      fetchContext(id);
    }
  }, [id]);

  const [
    activateErrors,
    activationInProgress,
    clearErrors,
    activateClinicalTrial,
  ] = useActivateClinicalTrialSlice((state, actions) => [
    state.errors,
    state.loading,
    actions.clearErrors,
    actions.activateClinicalTrial,
  ]);

  const fetchInventory = useInventorySlice(
    (_, actions) => actions.fetchInventory
  );

  const seg = (p?: string) => {
    if (!p) return '';
    const last = p.split('/').pop();
    return last ?? '';
  };

  const currentRoute = useMemo(
    () =>
      (visibleSubRoutes as Array<{ path?: string; key?: string }>).find(
        r => seg(r.path) === currentPathname || r.key === currentPathname
      ) as AppRouteType | undefined,
    [visibleSubRoutes, currentPathname]
  );

  const hasError = (route: AppRouteType) => {
    return !!activateErrors?.[route?.key as keyof ActivateState['errors']]
      ?.length;
  };
  const error = (route: AppRouteType) =>
    hasError(route) && (
      <div>
        <Attention width={16} height={16} className='text-vermilion' />
      </div>
    );

  const tabsItems = generateTabsItems({
    routes: visibleSubRoutes,
    currentPathname,
    id: id ?? '',
    children: error as ChildrenCreator,
  });

  const handleOpenConfirmModal = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const activate = () => {
    if (id) {
      activateClinicalTrial({ id, currentRoute: currentRoute?.key });
      setOpenModal(false);
    }
  };

  useEffect(() => {
    const splitPath = pathname.split('/');
    const clinicalTrialSubRouteName = splitPath[3];

    const parentRoute = visibleSubRoutes.find(r =>
      r.path?.includes(clinicalTrialSubRouteName)
    );

    if (parentRoute) {
      setCurrentPathname(clinicalTrialSubRouteName ?? '');
    } else {
      const pathN = splitPath[splitPath.length - 1];
      setCurrentPathname(pathN ?? '');
    }
  }, [pathname]);

  useEffect(() => {
    fetchInventory(undefined);
    return () => {
      clearErrors();
    };
  }, []);

  if (clinicalTrialLoading) {
    return (
      <div className='relative h-full w-full'>
        <PageLoading className='top-[50%]' />
      </div>
    );
  }

  if (clinicalTrialError) {
    return <Navigate to={routesPath.notFound} replace />;
  }

  return (
    <div
      key={resetKey}
      data-testid='ClinicalTrial'
      className={clsx(
        'D7-ClinicalTrial WRAP min-h-[calc(100%-5.6rem)] max-w-full'
      )}
    >
      <header className='mini:py-2 flex w-full items-center justify-between py-4'>
        <div className='flex max-w-full items-center gap-[.5rem]'>
          <BackButton action={goBack} text={'Back'} />
          <Tooltip
            color='white'
            className={'h-full'}
            title={
              <span className='text-1424 text-black'>{currentTrial?.name}</span>
            }
          >
            <h2 className='mini:text-[2.4rem] mini:leading-[2.8] max-w-[50rem] truncate text-left text-[3.6rem] font-semibold leading-[4.4rem] tracking-normal'>
              {currentTrial?.name}
            </h2>
          </Tooltip>

          <div className='status ml-2'>
            {currentTrial?.status && (
              <StatusPill status={currentTrial.status} />
            )}
          </div>
        </div>
        {currentTrial?.status === StudyStatusEnum.draft && !isPM && (
          <div
            data-testid='activateButton'
            className=' flex w-full min-w-0 justify-end gap-3'
          >
            <Button
              className={'mini:!px-2 mini:!text-1216 mini:!py-1.5 !px-4'}
              type='primary'
              disabled={activationInProgress}
              onClick={handleOpenConfirmModal}
            >
              Activate
            </Button>
          </div>
        )}
      </header>
      {currentPathname && (
        <div
          className={twMerge(
            'flex flex-col h-[calc(100%-110px)] min-h-[calc(100%-110px)] relative',
            shouldBeBlocked ? 'pb-[15rem]' : ''
          )}
        >
          <Tabs
            activeKey={currentPathname}
            blocked={blockerState === 'blocked' && shouldBeBlocked}
            items={tabsItems}
          />
          {currentRoute && hasError(currentRoute) && currentRoute.key && (
            <div className='mt-1 flex'>
              <div className='flex h-[48px] items-center gap-1 truncate rounded-sm border border-[#FF400F66] bg-[#FFF6F0] p-1.5 text-1424 font-medium text-vermilion'>
                <div>
                  <Attention
                    width={16}
                    height={16}
                    className='text-vermilion'
                  />
                </div>
                <CustomTooltip
                  label={
                    ACTIVATION_ERROR_MESSAGES[
                      currentRoute.key as keyof typeof ACTIVATION_ERROR_MESSAGES
                    ] || ACTIVATION_ERROR_MESSAGES.defaultMessage
                  }
                />
              </div>
            </div>
          )}
          {children}
        </div>
      )}
      <UnsavedChangesModal
        open={blockerState === 'blocked'}
        onConfirm={() => proceed()}
        onCancel={() => reset()}
      />
      {shouldBeBlocked && (
        <BottomConfirmation
          onCancel={() => {
            setResetKey(k => k + 1);
          }}
          onOk={() => {}}
          okText='Save'
          okProps={{
            htmlType: 'submit',
            form: 'clinical-trial',
          }}
          data-testid='formChangesConfirmationPanel'
        >
          You have made changes to the data. Do you want to save the changes?
        </BottomConfirmation>
      )}
      <ClinicalTrialActivateModal
        open={openModal}
        onCancel={handleCloseModal}
        onConfirm={activate}
      />
    </div>
  );
};
